
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React, { useMemo } from 'react';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import { Audio } from 'types';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';
import { getAudio } from '@/data/audios';
import CallToAction from '@/components/CallToAction';
import MarkdownBlock from '@/components/Chat/MarkdownBlock';
import AIContent from '@/components/landing/Transcription/AIContent';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import LogosCloud from '@/components/LogosCloud';
import AudioPlayer from '@/components/player/AudioPlayer';
import Seo from '@/components/Seo';
import TranscriptionFeaturesSimple from '@/components/TranscriptionFeaturesSimple';
import { AudioProvider, useAudioPlayer } from '@/contexts/AudioProvider';
const LecturePreset = ({ audio }: {
    audio: Audio;
}) => {
    const { t } = useLocale('ai-transcription');
    const audioPlayerData = useMemo(() => ({
        title: audio.name,
        audio: {
            src: audio.audioUrl,
            type: 'audio/mpeg',
        },
    }), [audio]);
    const player = useAudioPlayer({
        title: audio.title,
        audio: {
            src: audio.audioUrl,
            type: 'audio/mpeg',
        },
    });
    return (<AudioProvider meta={audio.audio_file_name ? audioPlayerData : null}>
      <main className='relative bg-gray-100 dark:bg-slate-900'>
        {/* <Banner /> */}
        <Seo templateTitle={t('AI Lecture Assistant | Transcribe, Summarize, Generate Notes, Create Study Materials')} description={t('Transform your lectures into comprehensive study materials. Our AI-powered lecture transcription service offers accurate transcription, concise summaries, detailed notes, and customized study aids.')} image={`https://easy-peasy.ai/api/og-template?title=${t('AI Lecture Assistant')}`}/>
        <div className='relative bg-white dark:bg-slate-900'>
          <div className='mx-auto'>
            <div className='relative z-10 bg-white pb-8 dark:bg-slate-900 lg:w-full'>
              <div className='relative bg-white dark:bg-slate-900'>
                <div className='mx-auto'>
                  <LandingPageNavigation />
                  <main className='mx-auto mt-10 flex justify-center px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 2xl:mt-28'>
                    <div className='text-center'>
                      <h1 className='flex justify-center text-4xl font-extrabold tracking-tight text-gray-900 dark:text-slate-200 sm:text-5xl lg:text-6xl'>
                        <div className='flex max-w-4xl flex-col items-center justify-center'>
                          <div className='mx-auto mb-4 w-fit rounded-full bg-indigo-500/10 px-3 py-1 text-sm font-semibold leading-6 tracking-wide text-primary-600 ring-1 ring-inset ring-indigo-500/20'>
                            {t('Comprehensive AI Lecture Solution')}
                          </div>
                          <div className='bg-gradient-to-r from-primary-600 to-pink-600 bg-clip-text pb-5 text-transparent'>
                            {t('AI-Powered Lecture Transcription, Summarization, and Study Material Generation')}
                          </div>
                        </div>
                      </h1>
                      <p className='mx-auto flex max-w-3xl justify-center text-xl text-gray-500 sm:text-2xl'>
                        {t('Convert your lectures into comprehensive study resources instantly. Our AI-driven lecture assistant transcribes, summarizes, and generates study materials, helping you focus on learning rather than note-taking.')}
                      </p>
                      <div className='mt-5 sm:mt-10 sm:flex sm:justify-center'>
                        <div>
                          <div className='rounded-md shadow'>
                            <Link href='/audios' className='flex w-full items-center justify-center rounded-md border border-transparent bg-primary-600 px-8 py-3 text-base font-medium text-white hover:bg-primary-700 md:px-10 md:py-4 md:text-lg'>
                              {t('Get started for free')}
                            </Link>
                          </div>
                          <div className='mt-5 flex items-center justify-center space-x-4'>
                            <div className='flex -space-x-3 overflow-hidden'>
                              <Image src='/images/ankita.png' alt='' width='32' height='32' className='v-lazy-image v-lazy-image-loaded inline-block aspect-[1/1] h-8 w-8 rounded-full border-[2px] border-indigo-400'/>
                              <Image src='/images/dmytro.jpg' alt='' width='32' height='32' className='v-lazy-image v-lazy-image-loaded inline-block aspect-[1/1] h-8 w-8 rounded-full border-[2px] border-indigo-400'/>
                              <Image src='/images/marianna_ole.jpeg' alt='' width='32' height='32' className='v-lazy-image v-lazy-image-loaded inline-block aspect-[1/1] h-8 w-8 rounded-full border-[2px] border-indigo-400'/>
                              <Image src='/images/andrea.webp' alt='' width='32' height='32' className='v-lazy-image v-lazy-image-loaded inline-block aspect-[1/1] h-8 w-8 rounded-full border-[2px] border-indigo-400'/>
                              <Image src='/images/cody.jpeg' alt='' width='32' height='32' className='v-lazy-image v-lazy-image-loaded inline-block aspect-[1/1] h-8 w-8 rounded-full border-[2px] border-indigo-400'/>
                            </div>{' '}
                            <p className='mx-auto max-w-lg text-sm  text-gray-500 lg:mx-0'>
                              <strong>876,388+</strong> {t('happy users')}
                            </p>{' '}
                            <div className='convert-13-v1 flex'>
                              <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4 text-yellow-400'>
                                <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z'></path>
                              </svg>
                              <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4 text-yellow-400'>
                                <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z'></path>
                              </svg>
                              <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4 text-yellow-400'>
                                <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z'></path>
                              </svg>
                              <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4 text-yellow-400'>
                                <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z'></path>
                              </svg>
                              <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4 text-yellow-400'>
                                <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z'></path>
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='mt-10 flex w-full justify-center gap-x-4 gap-y-2 text-center text-gray-500 dark:text-slate-400 lg:text-lg'>
                        <div>🚀 {t('Powered by best AI models')}</div>
                        <div>🌍 {t('Supports 40+ languages')}</div>
                        <div>💳 {t('No credit card required')}</div>
                        {/* <div>⭐ {t('5-star ratings')}</div> */}
                      </div>
                    </div>
                  </main>
                </div>
              </div>
            </div>
          </div>
        </div>
        <LogosCloud />
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <div className='px-4 xl:px-8 2xl:px-16'>
            <h2 className='mb-10 mt-10 text-2xl font-bold text-primary-600'>
              Transcript
            </h2>
            <div className='relative  w-full rounded-md bg-gradient-to-r from-purple-600 via-red-500 to-primary-600 p-2 shadow-lg lg:max-w-xl'>
              <LiteYouTubeEmbed id='NNnIGh9g6fA' title='Introduction to Human Behavioral Biology' poster='hqdefault'/>
            </div>
            <div className='message mt-5 flex '>
              <MarkdownBlock message={audio.content ||
            t('Transcription is in progress... It may take a 10-20 minutes if the audio is long.')}/>
            </div>
          </div>
          <div className='px-4 xl:px-8 2xl:px-16'>
            <AIContent audio={audio}/>
          </div>
        </div>

        <TranscriptionFeaturesSimple />
        {/* <FeaturedLogos /> */}
        {/* <FaqTranscription /> */}
        {/* <Testimonials /> */}
        {/* <ContactUs /> */}
        <CallToAction preset='audios'/>
        <Footer />
      </main>
      <div className='fixed inset-x-0 bottom-0 z-10 '>
        <AudioPlayer />
      </div>
    </AudioProvider>);
};
export default LecturePreset;
// add static props
async function getStaticProps() {
    const audio = await getAudio('62af0b77-ef3c-4801-ae2d-9fecf96a9e4d', '27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e');
    return {
        props: { audio },
    };
}

    async function __Next_Translate__getStaticProps__1a11b03d483__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-transcription/lecture',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a11b03d483__ as getStaticProps }
  